import React, { useEffect, useMemo, useRef, useState } from 'react';
import { safeHref } from '../../utils/common/safe-url';
import ReactPlayer from 'react-player';
import { AdCreative } from '../../api/ads/ads.api';
import { usePreRoll } from '../ads/use-pre-roll';
import PreRollAd from '../ads/pre-roll-ad';
import { releasePreRollSlot } from '../ads/pre-roll-frequency';

interface Hotspot { label: string; url: string; start: number; end: number }

type PreRollPhase = 'idle' | 'serving' | 'ad' | 'done';

/** Trang hoặc chính video đang toàn màn hình (lớp phủ không hiện được trong đó): không chèn quảng cáo. */
export const isFullscreen = (video?: HTMLVideoElement | null) => {
  const doc = document as any;
  return !!(doc.fullscreenElement || doc.webkitFullscreenElement || (video as any)?.webkitDisplayingFullscreen);
};

/**
 * Player video + lớp phủ "thẻ mua sắm" tương tác (Interactive Hotspots / Shoppertainment).
 * Hiện nút bấm được (link sản phẩm/gói VIP) chồng lên video trong khoảng [start,end] khi đang phát.
 * Không có hotspot → hoạt động y như ReactPlayer thường (additive, không đổi hành vi post khác).
 *
 * Có `preRoll` (bài video trên bảng tin, cửa sổ chi tiết, trang /videos): lúc người xem bấm xem lần đầu, hỏi quảng cáo chèn
 * trước (tối đa 1,5 giây, video chính dừng trong lúc chờ). Có quảng cáo thì phủ lên trình phát; bỏ qua, xem hết hoặc quảng cáo
 * hỏng thì video chính chạy lại từ đầu với điều khiển bình thường. Không có quảng cáo thì video chính chạy ngay.
 */
export const VideoWithShopHotspots: React.FC<{
  url?: string;
  hotspotsJson?: string | null;
  config?: any;
  width?: string | number;
  playing?: boolean;
  preRoll?: { postId?: string | null; ownerId?: string | null } | null;
}> = ({ url, hotspotsJson, config, width, playing = false, preRoll }) => {
  const [t, setT] = useState(0);
  const playerRef = useRef<ReactPlayer>(null);
  const [mainPlaying, setMainPlaying] = useState(playing);
  const [phase, setPhase] = useState<PreRollPhase>('idle');
  const [ad, setAd] = useState<AdCreative | null>(null);
  const phaseRef = useRef<PreRollPhase>('idle');
  phaseRef.current = phase;
  const alive = useRef(true);
  const { begin } = usePreRoll({ postId: preRoll?.postId, ownerId: preRoll?.ownerId });

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => setMainPlaying(playing), [playing]);
  // Đổi video thì lần bấm xem sau được xét lại.
  useEffect(() => {
    setPhase('idle');
    setAd(null);
  }, [url, preRoll?.postId]);

  const hotspots = useMemo<Hotspot[]>(() => {
    if (!hotspotsJson) return [];
    try {
      const a = JSON.parse(hotspotsJson);
      return Array.isArray(a) ? a : [];
    } catch {
      return [];
    }
  }, [hotspotsJson]);

  const internal = () => playerRef.current?.getInternalPlayer() as HTMLVideoElement | undefined;
  const pauseMain = () => {
    try {
      internal()?.pause?.();
    } catch {
      /* bỏ qua */
    }
  };
  const playMain = (fromStart: boolean) => {
    if (fromStart) playerRef.current?.seekTo(0, 'seconds');
    setMainPlaying(true);
    try {
      internal()?.play?.()?.catch?.(() => undefined);
    } catch {
      /* bỏ qua */
    }
  };

  const onMainPlay = () => {
    const current = phaseRef.current;
    // Đang chờ hoặc đang chạy quảng cáo: video chính không được chạy chen.
    if (current === 'serving' || current === 'ad') {
      pauseMain();
      return;
    }
    if (current === 'done' || !preRoll?.postId) return;
    if (isFullscreen(internal())) {
      setPhase('done');
      return;
    }
    const pending = begin();
    if (!pending) {
      setPhase('done');
      return;
    }
    pauseMain();
    setMainPlaying(false);
    setPhase('serving');
    phaseRef.current = 'serving';
    pending.then((found) => {
      if (!alive.current || phaseRef.current !== 'serving') {
        if (found) releasePreRollSlot(); // trình phát đã gỡ hoặc đổi video: quảng cáo không hiện, trả lượt hỏi
        return;
      }
      if (found) {
        setAd(found);
        setPhase('ad');
      } else {
        setPhase('done');
        playMain(false);
      }
    });
  };

  const onAdFinish = () => {
    setAd(null);
    setPhase('done');
    playMain(true);
  };

  const active = hotspots.filter((h) => h?.url && h?.label && t >= (h.start || 0) && t <= (h.end || 0));

  return (
    <div className="relative">
      <ReactPlayer
        ref={playerRef}
        playing={mainPlaying}
        url={url}
        loop
        controls
        width={width}
        config={config}
        onPlay={onMainPlay}
        onProgress={(s) => setT(s.playedSeconds)}
        progressInterval={500}
      />
      {phase === 'serving' ? (
        // Chặn bấm vào trình phát trong lúc hỏi quảng cáo (tối đa 1,5 giây).
        <div className="absolute inset-0 z-30" data-testid="pre-roll-waiting" onClick={(e) => e.stopPropagation()} />
      ) : null}
      {phase === 'ad' && ad ? <PreRollAd ad={ad} onFinish={onAdFinish} /> : null}
      {active.length > 0 && (
        <div className="pointer-events-none absolute left-0 right-0 bottom-14 flex flex-wrap justify-center gap-2 px-3">
          {active.map((h, i) => (
            <a
              key={i}
              href={safeHref(h.url)}
              target="_blank"
              rel="noreferrer"
              className="pointer-events-auto rounded-full bg-[#0284c7] px-4 py-1.5 text-sm font-semibold text-white shadow-lg hover:brightness-110"
            >
              🛒 {h.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
